import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { useAppContextNonNull } from "../../../contexts/AppContext";
import IVendor_Source from "../../../models/IVendor_Source";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IVendor_Source_Location from "../../../models/IVendor_Source_Location";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import OwGoogleStaticMap from "../../shared/OwGoogleStaticMap/OwGoogleStaticMap";
import { LoadingResults } from "../../shared/LoadingResults/LoadingResults";
import IntegrationsConnectedBar from "../../shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import OwTable from "../../shared/OwTable/OwTable";
import IOwTableCols from "../../../models/IOwTableCols";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import "./VendorSourceDetailView.scss";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import IOnGetVendorSourceCatalogItemsDTO from "@models/DTO/IOnGetVendorSourceCatalogItemsDTO";
import ICatalog_Item_Vendor_Source_Map from "@models/ICatalog_Item_Vendor_Source_Map";
import { SearchSection } from "../../shared/SearchSection/SearchSection";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import { VendorSourceAddCatalogItem } from "../VendorSourceAddCatalogItem";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import IOnDeleteVendorSourceCatalogItemDTO from "@models/DTO/IOnDeleteVendorSourceCatalogItemDTO";
import { VendorSourceFnSidebar } from "../VendorSourceFnSidebar/VendorSourceFnSidebar";

type Props = {};

export const VendorSourceDetailView: React.FC<Props> = (props) => {
    // Context
    const { socket, userModel, supplierModel, uiContext } = useAppContextNonNull();
    const match = useMatch("/vendor_sources/:guid/detail");
    const navigate = useNavigate();
    const guid = match?.params.guid || "";

    // State
    const [vendorSource, setVendorSource] = useState<IVendor_Source | null>(null);
    const [locations, setLocations] = useState<IVendor_Source_Location[]>([]);
    const [catalogItems, setCatalogItems] = useState<ICatalog_Item_Vendor_Source_Map[]>([]);
    const [totalCatalogItems, setTotalCatalogItems] = useState(0);
    const [catalogItemsOffset, setCatalogItemsOffset] = useState(0);
    const [catalogItemsLimit, setCatalogItemsLimit] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [isCatalogItemsLoading, setIsCatalogItemsLoading] = useState(false);
    const [isCatalogItemsInitialized, setIsCatalogItemsInitialized] = useState(false);
    const [skuFilter, setSkuFilter] = useState<string>("");
    const [nameFilter, setNameFilter] = useState<string>("");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [deleteRow, setDeleteRow] = useState<ICatalog_Item | undefined>();
    const [targetDeleteElem, setTargetDeleteElem] = useState<string | null>(null);

    // Fetch vendor source
    const getVendorSource = useCallback(async () => {
        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.GetVendorSource,
                params: { vendorSourceGuid: guid },
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, { headers });
            const data = await response.json();

            if (data.success && data.vendorSource) {
                setVendorSource(data.vendorSource);
                document.title = `Orderwerks >> ${data.vendorSource.name} >> Detail`;
            } else {
                uiContext.notifyFn(null, {
                    message: "Failed to load vendor source",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                });
                navigate("/vendor_sources");
            }
        } catch (error) {
            console.error("Failed to fetch vendor source:", error);
            uiContext.notifyFn(null, {
                message: "Failed to load vendor source",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
            navigate("/vendor_sources");
        } finally {
            setIsLoading(false);
        }
    }, [guid, userModel, supplierModel, navigate, uiContext]);

    // Fetch vendor source locations
    const getVendorSourceLocations = useCallback(async () => {
        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.GetVendorSourceLocations,
                params: { vendorSourceGuid: guid },
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, { headers });
            const data = await response.json();

            if (data.success && data.locations) {
                setLocations(data.locations);
            }
        } catch (error) {
            console.error("Failed to fetch vendor source locations:", error);
        }
    }, [guid, userModel, supplierModel]);

    // Fetch catalog items with pagination
    const getCatalogItems = async () => {
        setIsCatalogItemsLoading(true);
        try {
            const query: Record<string, any> = {
                limit: catalogItemsLimit,
                offset: catalogItemsOffset,
            };

            // Add filters if they have values
            if (skuFilter.length) {
                query.sku = skuFilter;
            }

            if (nameFilter.length) {
                query.name = nameFilter;
            }

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.GetVendorSourceCatalogItems,
                params: { vendorSourceGuid: guid },
                query,
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, { headers });
            const data = (await response.json()) as IOnGetVendorSourceCatalogItemsDTO;

            if (data.success) {
                setCatalogItems(data.catalogItems || []);
                setTotalCatalogItems(data.totalCatalogItems || 0);
                setIsCatalogItemsInitialized(true);
                setIsCatalogItemsLoading(false);
            }
        } catch (error) {
            console.error("Failed to fetch catalog items:", error);
        } finally {
            setIsCatalogItemsLoading(false);
        }
    };

    // Search handler functions
    const handleChange = useCallback((e: ChangeEvent<any>) => {
        const value = e.target.value;
        const fieldName = e.target.name;

        if (fieldName === "sku") {
            setSkuFilter(value);
        } else if (fieldName === "name") {
            setNameFilter(value);
        }
    }, []);

    const search = () => {
        setCatalogItemsOffset(0); // Reset to first page when searching
        setRefreshTrigger((prev) => prev + 1);
    };

    const reset = () => {
        setSkuFilter("");
        setNameFilter("");
        setCatalogItemsOffset(0);
        setCatalogItemsLimit(25);
        setRefreshTrigger((prev) => prev + 1);
    };

    const enterSearch = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            search();
        }
    }, []);

    // Effects
    useEffect(() => {
        getVendorSource();
        getVendorSourceLocations();
    }, [getVendorSource, getVendorSourceLocations]);

    useEffect(() => {
        if (vendorSource) {
            getCatalogItems();
        }
    }, [vendorSource, catalogItemsOffset, catalogItemsLimit, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pagination handler
    const onCatalogItemsPaginationChanged = (offset: number, limit: number) => {
        setCatalogItemsOffset(offset);
        setCatalogItemsLimit(limit);
    };

    const toggleConfirmDelete = (row: ICatalog_Item, index: number) => {
        setDeleteRow(row);
        setTargetDeleteElem(`row-${index}`);
    };

    const cancelDelete = () => {
        setDeleteRow(undefined);
        setTargetDeleteElem(null);
    };

    const removeCatalogItem = async (row: ICatalog_Item) => {
        if (deleteRow) {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.DeleteVendorSourceCatalogItem,
                params: { vendorSourceGuid: guid, catalogItemGuid: deleteRow.guid },
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const response = await fetch(url, { headers, method: "DELETE" });
            const data = (await response.json()) as IOnDeleteVendorSourceCatalogItemDTO;

            if (data.success) {
                getCatalogItems();
                setDeleteRow(undefined);
                setTargetDeleteElem(null);
            } else {
                uiContext.notifyFn(null, {
                    message: "Failed to delete catalog item",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                });
            }
        }
    };

    // Catalog items table columns
    const catalogItemColumns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "",
            isActionsCol: true,
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map, index: number) => {
                return (
                    <div>
                        <OwDeleteEditActions
                            isEditMode={false}
                            onTrashClickFn={() => {
                                if (item.Catalog_Item) {
                                    toggleConfirmDelete(item.Catalog_Item, index);
                                }
                            }}
                            onEditClickFn={() => {
                                showAddCatalogItemSidebar(item);
                            }}
                        />
                    </div>
                );
            },
        },
        {
            title: "catalog",
            valueProp: "Catalog",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => item.Catalog_Item?.Catalog?.name || "-",
        },
        {
            title: "name",
            valueProp: "name",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => item.Catalog_Item?.name || "-",
        },
        {
            title: "mpn",
            valueProp: "manuf_part_no",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => {
                return item.manuf_part_no || "-";
            },
        },
        {
            title: "cost_price",
            valueProp: "cost_price",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => {
                if (item.cost_price) {
                    return `$${Number(item.cost_price).toFixed(2)}`;
                }
                return "-";
            },
        },
        {
            title: "sku",
            valueProp: "sku",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => item.Catalog_Item?.sku || "-",
        },
        {
            title: "upc",
            valueProp: "upc",
            renderFnEmitsRow: true,
            renderFn: (item: ICatalog_Item_Vendor_Source_Map) => item.Catalog_Item?.upc || "-",
        },
    ];

    // Location table columns
    const locationColumns: IOwTableCols[] = [
        {
            title: "Name",
            valueProp: "name",
            renderFn: (location: IVendor_Source_Location) => location.name || "-",
        },
        {
            title: "Address",
            valueProp: "address",
            renderFn: (location: IVendor_Source_Location) => {
                if (location.Address) {
                    return (
                        <div className="d-flex">
                            <div className="flex-grow-1">
                                <AddressDisplay address={location.Address} />
                            </div>
                            {supplierModel.has_google_maps && (
                                <div className="ms-2">
                                    <OwGoogleStaticMap address={location.Address} />
                                </div>
                            )}
                        </div>
                    );
                }
                return "-";
            },
        },
    ];

    const showAddCatalogItemSidebar = (existingCatalogItemMap?: ICatalog_Item_Vendor_Source_Map) => {
        const component = (
            <VendorSourceAddCatalogItem
                existingCatalogItemMap={existingCatalogItemMap}
                vendorSourceGuid={guid}
                onItemAdded={() => setRefreshTrigger((prev) => prev + 1)}
                cancelFn={() => {
                    uiContext.setSidebarComponent(null);
                    uiContext.toggleSidebar(false);
                }}
            />
        );
        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    if (isLoading) {
        return <LoadingResults />;
    }

    if (!vendorSource) {
        return (
            <div className="VendorSourceDetailView">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <OwTitle title="vendor_source_not_found" tag="h3" />
                            <Breadcrumbs
                                crumbs={[
                                    { title: "Vendor Sources", url: "/vendor_sources" },
                                    { title: "Detail", url: "" },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const cancelSidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        getVendorSource();
    };

    return (
        <div className="VendorSourceDetailView">
            <div className="container-fluid">
                {/* Header Section */}
                <div className="row mb-3">
                    <div className="col-12">
                        <Breadcrumbs
                            crumbs={[
                                { title: "Vendor Sources", url: "/vendor_sources" },
                                { title: vendorSource.name, url: "", skipTranslate: true },
                            ]}
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-12">
                        <div className="d-flex align-items-center">
                            <OwTitle title={vendorSource.name} disableTranslation={true} />
                            <OwDeleteEditActions
                                className="ms-2"
                                isEditMode={false}
                                hideTrash={true}
                                onEditClickFn={() => {
                                    const component = <VendorSourceFnSidebar cancelFn={cancelSidebar} vendorSource={vendorSource} />;

                                    uiContext.setSidebarComponent(component);
                                    uiContext.toggleSidebar(true);
                                }}
                                onTrashClickFn={() => {}}
                            />
                        </div>

                        {vendorSource.customer_no && (
                            <div className="customer-no">
                                <OwLabel title="customer_no" /> {vendorSource.customer_no}
                            </div>
                        )}
                    </div>
                </div>

                {/* QuickBooks Integration Badges */}
                {(vendorSource.qbo_VendorId || vendorSource.qbd_ListID) && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <IntegrationsConnectedBar
                                supplierModel={supplierModel}
                                qboConnected={!!vendorSource.qbo_VendorId}
                                qbdConnected={!!vendorSource.qbd_ListID}
                            />
                        </div>
                    </div>
                )}

                {/* Addresses Section */}
                {(vendorSource.Bill_To || vendorSource.Ship_To) && (
                    <div className="row mb-4">
                        {vendorSource.Bill_To && (
                            <div className="col-12 col-md-6 mb-3">
                                <div className="address-section">
                                    <OwTitle title="bill_to" tag="h5" />
                                    <AddressDisplay address={vendorSource.Bill_To} />
                                    {supplierModel.has_google_maps && (
                                        <div className="mt-2">
                                            <OwGoogleStaticMap address={vendorSource.Bill_To} className="d-inline-flex" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {vendorSource.Ship_To && (
                            <div className="col-12 col-md-6 mb-3">
                                <div className="address-section">
                                    <OwTitle title="ship_to" tag="h5" />
                                    <AddressDisplay address={vendorSource.Ship_To} />
                                    {supplierModel.has_google_maps && (
                                        <div className="mt-2">
                                            <OwGoogleStaticMap address={vendorSource.Ship_To} className="d-inline-flex" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Catalog Items Search Section */}
                <div className="row mb-3">
                    <div className="col-12">
                        <SearchSection>
                            <div className="d-flex">
                                <div className="w-15r">
                                    <OwLabel title={"sku"} noBottomMargin={true} />
                                    <OwFnInput
                                        name={"sku"}
                                        value={skuFilter}
                                        fn={handleChange}
                                        onKeyDownFn={enterSearch}
                                        placeholder={"search_by_sku"}
                                    />
                                </div>

                                <div className="ms-2 w-15r">
                                    <OwLabel title={"name"} noBottomMargin={true} />
                                    <OwFnInput
                                        name={"name"}
                                        value={nameFilter}
                                        fn={handleChange}
                                        onKeyDownFn={enterSearch}
                                        placeholder={"search_by_name"}
                                    />
                                </div>

                                <div className="ms-2">
                                    <OwLabel title={""} noBottomMargin={true} />
                                    <div>
                                        <OwButton title={"search"} fn={search} />
                                        <OwButton title={"reset"} fn={reset} className={"btn-danger ms-1"} />
                                    </div>
                                </div>
                            </div>
                        </SearchSection>
                    </div>
                </div>

                {/* Catalog Items Section */}
                <div className="row mb-4 ">
                    <div className="col-12">
                        <div className="section-container position-relative">
                            <ConfirmDeleteOverlayFn
                                handleConfirm={() => {
                                    removeCatalogItem(deleteRow!);
                                }}
                                handleCancel={cancelDelete}
                                targetElem={targetDeleteElem}
                                message={"confirm_delete"}
                                // leftAdjustment={-12}
                                nestedParentElem={`vendor-source-items-table-${guid}`}
                                skipMainView={true}
                            />

                            <div className="d-flex align-items-center">
                                <OwTitle title="catalog_items" tag="h5" />
                                <div className="ms-2">
                                    <OwButton
                                        leftIconClass={"fas fa-plus"}
                                        className={"btn-warning"}
                                        title={"add"}
                                        fn={() => {
                                            showAddCatalogItemSidebar();
                                        }}
                                    />
                                </div>
                            </div>
                            {isCatalogItemsLoading ? (
                                <LoadingResults />
                            ) : catalogItems.length > 0 ? (
                                <>
                                    <div>
                                        <OwTable
                                            id={`vendor-source-items-table-${guid}`}
                                            columns={catalogItemColumns}
                                            dataSet={catalogItems}
                                            dataFetch={isCatalogItemsLoading}
                                            initialData={isCatalogItemsInitialized}
                                            totalRows={totalCatalogItems}
                                            userModel={userModel}
                                            supplierModel={supplierModel}
                                        />
                                    </div>

                                    <div className="mt-3">
                                        <PaginationBar
                                            offset={catalogItemsOffset}
                                            limit={catalogItemsLimit}
                                            totalRecords={totalCatalogItems}
                                            onPaginationChanged={onCatalogItemsPaginationChanged}
                                            isActive={isCatalogItemsLoading}
                                        />
                                    </div>
                                </>
                            ) : (
                                <p className="text-muted">No catalog items found for this vendor source.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Locations Section */}
                {/* <div className="row mb-4">
                    <div className="col-12">
                        <div className="section-container">
                            <h4>Locations</h4>
                            {locations.length > 0 ? (
                                <OwFnTable columns={locationColumns} dataSet={locations} dataFetch={false} initialData={false} />
                            ) : (
                                <p className="text-muted">No locations found for this vendor source.</p>
                            )}
                        </div>
                    </div>
                </div> */}
            </div>
        </div>
    );
};
